import {
  GETHospitalWardList,
  GETJobGroups,
  GETPosts,
  GETSearchUsers,
} from "api/user";
import {
  IGETJobGroupsResponse,
  IGETPostsResponse,
  ISearchUser,
} from "api/user/types";
import { Button } from "components/button/button";
import { CheckBox } from "components/checkbox/check-box";
import { Input } from "components/input/input";
import { Modal } from "components/modal/modal";
import { SelectInput } from "components/select/select";
import { Table } from "components/table/table";
import { useEffect, useMemo, useState, type FC } from "react";
import { GoSearch } from "react-icons/go";
import { useDispatch, useSelector } from "react-redux";
import { changeWardList } from "state-manager/reducer/hospital";
import { RootState } from "state-manager/store";
import { useSelectUser } from "./use-select-user";
import { UserSelectModalContainer } from "./user-select-modal.style";

interface IUserSelectModalProps {
  selecteds?: number[];
  onClose: () => void;
  onSubmit: (selectedUsers: number[]) => void;
  locked?: boolean;
}

interface IFilterValues {
  query: string;
  post: IGSelectOption | null;
  job_location: IGSelectOption | null;
  job_group: IGSelectOption | null;
}

interface ILoadingValues {
  ward: boolean;
  users: boolean;
  post: boolean;
  job_group: boolean;
}

export const UserSelectModal: FC<IUserSelectModalProps> = ({
  selecteds = [],
  onClose,
  onSubmit,
  locked = false,
}) => {
  const dispatch = useDispatch();
  const { wardList, hospitalId } = useSelector(
    (state: RootState) => state.hospital
  );
  const [users, setUsers] = useState<ISearchUser[] | null>(null);
  const [jobGroups, setJobGroups] = useState<IGETJobGroupsResponse[] | null>(
    null
  );
  const [posts, setPosts] = useState<IGETPostsResponse["posts"] | null>(null);
  const [loadings, setLoadings] = useState<ILoadingValues>({
    ward: false,
    users: false,
    post: false,
    job_group: false,
  });
  const [filterValues, setFilterValues] = useState<IFilterValues>({
    query: "",
    post: null,
    job_location: null,
    job_group: null,
  });

  const { selectAllUsers, selectedUsers, toggleUser } = useSelectUser({
    users,
    selecteds,
  });

  // get wards list if it's empty
  useEffect(() => {
    if (wardList.length !== 0) return;

    onChangeLoading("ward", true);
    GETHospitalWardList({ hospitalId })
      .then((res) => {
        dispatch(
          changeWardList(res.filter(({ name }) => name !== "بخش ناشناس"))
        );
      })
      .finally(() => onChangeLoading("ward", false));
  }, [hospitalId, wardList, dispatch]);

  useEffect(() => {
    onChangeLoading("job_group", true);
    GETJobGroups()
      .then((res) => {
        setJobGroups(res);
      })
      .finally(() => onChangeLoading("job_group", false));
  }, []);

  useEffect(() => {
    onChangeLoading("post", true);
    GETPosts()
      .then((res) => {
        setPosts(res.posts);
      })
      .finally(() => onChangeLoading("post", false));
  }, []);

  // get all users
  useEffect(() => {
    const params = {
      hospitalId,
      ...(filterValues.query && { query: filterValues.query }),
      ...(filterValues.post && { post: filterValues.post.value }),
      ...(filterValues.job_location && {
        job_location: filterValues.job_location.value,
      }),
      ...(filterValues.job_group && {
        job_group: filterValues.job_group.value,
      }),
    };

    onChangeLoading("users", true);
    GETSearchUsers(params)
      .then((res) => {
        if (selecteds.length) {
          const selectedsSet = new Set(selecteds); // Convert selecteds to a Set for O(1) lookups

          const sortedUsers = (res ?? []).sort((a, b) => {
            const isASelected = selectedsSet.has(a.id);
            const isBSelected = selectedsSet.has(b.id);

            if (isASelected && !isBSelected) return -1; // `a` should come first
            if (!isASelected && isBSelected) return 1; // `b` should come first
            return 0; // Keep the original order otherwise
          });

          setUsers(sortedUsers);
        } else {
          setUsers(res);
        }
      })
      .finally(() => onChangeLoading("users", false));
  }, [
    filterValues.query,
    filterValues.job_location,
    filterValues.post,
    filterValues.job_group,
  ]);

  const usersData = useMemo(() => {
    if (!users) return null;

    return users.map((user) => [
      <CheckBox
        checked={!!selectedUsers?.includes(user.id)}
        onClick={() => toggleUser(user.id, locked)}
        className="d-flex justify-content-center align-items-center"
      />,
      `${user.fn} ${user.ln}`,
      user.post || "-",
    ]);
  }, [users, selectedUsers]);

  const headers = useMemo(() => {
    return [
      {
        child: (
          <CheckBox
            checked={
              users?.length !== 0 && users?.length === selectedUsers?.length
            }
            onClick={() => selectAllUsers(locked)}
            className="d-flex justify-content-center align-items-center"
            disabled={loadings.users}
          />
        ),
      },
      { child: "نــام و نــام خانوادگـــی" },
      { child: "سمت سازمانی" },
    ];
  }, [users, selectedUsers, selectAllUsers, loadings.users]);

  const wardOptions = useMemo(() => {
    return wardList.map((ward) => ({
      label: ward.name,
      value: ward.name,
    }));
  }, [wardList]);

  const jobGroupsOptions = useMemo(() => {
    return (
      jobGroups?.map((jobGroup) => ({
        label: jobGroup.name,
        value: jobGroup.name,
      })) || []
    );
  }, [jobGroups]);

  const postsOptions = useMemo(() => {
    return (
      posts?.map((post) => ({
        label: post,
        value: post,
      })) || []
    );
  }, [posts]);

  const onChangeFilter = (
    name: keyof IFilterValues,
    value: string | IGSelectOption | null
  ) => {
    setFilterValues({
      ...filterValues,
      [name]: value,
    });
  };

  const onChangeLoading = (name: keyof ILoadingValues, value: boolean) => {
    setLoadings((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    onSubmit(selectedUsers);
    onClose();
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="انتخاب / مشاهده کاربر"
      size="xl"
    >
      <UserSelectModalContainer>
        <Input
          title="جستجو نام و نام خانوادگی"
          value={filterValues.query}
          onChange={(v) => onChangeFilter("query", v)}
          icon={<GoSearch className="date_icon" size={20} />}
          placeholder="نام و نام خانوادگی را وارد کنید"
        />
        <div className="filters_parent">
          <SelectInput
            title="فیلتر بر اساس بخش"
            isLoading={loadings.ward}
            options={wardOptions}
            value={filterValues.job_location}
            onChange={(ob) => onChangeFilter("job_location", ob)}
            placeholder="بخش را انتخاب کنید"
            containerClassName="job_location"
            isClearable
          />
          <SelectInput
            title="فیلتر بر اساس گروه شغلی"
            isLoading={loadings.job_group}
            options={jobGroupsOptions}
            value={filterValues.job_group}
            onChange={(ob) => onChangeFilter("job_group", ob)}
            placeholder="گروه شغلی را انتخاب کنید"
            containerClassName="job_group_filter"
            isClearable
          />
          <SelectInput
            title="فیلتر بر اساس سمت"
            isLoading={loadings.post}
            options={postsOptions}
            value={filterValues.post}
            onChange={(ob) => onChangeFilter("post", ob)}
            placeholder="بخش را انتخاب کنید"
            containerClassName="post_filter"
            isClearable
          />
        </div>
        <div className="list_parent">
          <Table data={usersData} headers={headers} />
        </div>
        <div className="submit_parent">
          <Button className="submit_btn" onClick={handleSubmit}>
            ثبت
          </Button>
          <Button variant="outlined" className="close_btn" onClick={onClose}>
            بازگشت
          </Button>
        </div>
      </UserSelectModalContainer>
    </Modal>
  );
};
